/**
 * MobileNativeHeader
 * Gradient top-bar for native Android screens.
 * Replaces the web-style "[ Title ]" heading on all mobile screens.
 *
 * Light: purple→violet gradient with white text
 * Has an optional greeting (Home screen) or back button (detail screens).
 */
import { ChevronLeft } from 'lucide-react';
import React from 'react';

interface MobileNativeHeaderProps {
  /** Primary title text */
  title: string;
  /** Optional smaller subtitle below title */
  subtitle?: string;
  /** Show a back chevron on the left */
  showBack?: boolean;
  /** Called when back button is pressed */
  onBack?: () => void;
  /** Optional element to render in the top-right corner (e.g. avatar, icon) */
  rightSlot?: React.ReactNode;
}

const MobileNativeHeader: React.FC<MobileNativeHeaderProps> = ({
  title,
  subtitle,
  showBack = false,
  onBack,
  rightSlot,
}) => (
  <div
    style={{
      /*
       * Theme: light = purple (#8B80F9, see public/mobile/light.html); dark = dark gradient (see public/mobile/dark.html).
       */
      background: 'var(--ggj-header-bg)',
      minHeight: 56,
      paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 12px)',
      paddingBottom: 12,
      paddingLeft: 20,
      paddingRight: 20,
      borderRadius: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 0,
      boxShadow: '0 1px 0 rgba(0,0,0,0.08)',
      position: 'relative',
    }}
  >
    {/* Back button */}
    {showBack && (
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'rgba(255,255,255,0.18)',
          border: 'none',
          borderRadius: 10,
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
          WebkitTapHighlightColor: 'transparent',
        }}
        aria-label="Go back"
      >
        <ChevronLeft size={20} color="white" />
      </button>
    )}

    {/* Text block */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <h1
        style={{
          fontSize: 20,
          fontWeight: 800,
          color: 'var(--ggj-header-text, #ffffff)',
          margin: 0,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            fontSize: 13,
            color: 'var(--ggj-header-sub, rgba(255,255,255,0.75))',
            margin: '3px 0 0',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </p>
      )}
    </div>

    {/* Right slot */}
    {rightSlot && (
      <div style={{ flexShrink: 0 }}>
        {rightSlot}
      </div>
    )}
  </div>
);

export default MobileNativeHeader;
